import {
  AvailableRecordTypeFragment,
  FieldType,
} from '../../__generated__/graphql';
import {
  ExtraFieldInternalValue,
  mapFormValueToInternalValue,
  mapInternalValueToFormValue,
} from './extraFieldInternalValue';

export const recordTypeFormKey = 'extraFields_recordTypeName';

// Picks the configs that apply to a form or table, deduped by field. A config with no
// scope id is a wildcard that any scoped config overrides; `scopeIds` are in ascending
// precedence, so requesting ['X.Edit', 'X.ManagerDetails'] means ManagerDetails wins for
// a field configured under both. Input order is preserved — the server sorts by position.
export const selectScopedFields = <T extends { fieldApiName: string }>(
  fields: readonly T[],
  scopeIds: readonly string[],
  getScopeId: (field: T) => string | null,
): T[] => {
  const byFieldApiName = new Map<string, { field: T; precedence: number }>();
  fields.forEach((field) => {
    const scopeId = getScopeId(field);
    // 0 is the wildcard, so scoped configs start at 1
    const precedence = scopeId === null ? 0 : scopeIds.indexOf(scopeId) + 1;
    if (scopeId !== null && precedence === 0) {
      return;
    }
    const existing = byFieldApiName.get(field.fieldApiName);
    // among equally scoped configs the later one wins, but a repeated wildcard keeps
    // the first, matching how these were resolved before
    const wins =
      !existing ||
      precedence > existing.precedence ||
      (precedence === existing.precedence && precedence > 0);
    if (wins) {
      byFieldApiName.set(field.fieldApiName, { field, precedence });
    }
  });
  return Array.from(byFieldApiName.values(), (entry) => entry.field);
};

// From form to ExtraFieldInternalValue
export const collectFormDataExtraFields = (
  formData: Record<string, any>,
): ExtraFieldInternalValue[] => {
  const result: ExtraFieldInternalValue[] = [];
  const recordTypeName = formData[recordTypeFormKey];
  if (recordTypeName) {
    result.push({
      fieldApiName: 'RecordTypeName',
      type: FieldType.Textarea,
      internalValue: { value: recordTypeName },
    });
  }
  const otherFields = Object.keys(formData)
    .filter((key) => key.startsWith('customerField:'))
    .map((key) => {
      const { fieldApiName, type } = parseFieldApiName(key);
      return {
        fieldApiName,
        type,
        internalValue: mapFormValueToInternalValue(formData[key], type),
      };
    });
  result.push(...otherFields);
  return result;
};

//inverse of collectFormDataExtraFields
export const getFormValuesFromExtraFields = (
  extraFields: readonly ExtraFieldInternalValue[], // in TS the same as ExtraFieldInput
  recordTypeName: string, // because it is not presented in ExtraFieldValueShortFragment
): Record<string, unknown> => {
  const formValues = extraFields.reduce((acc, v) => {
    if (v.fieldApiName === 'RecordTypeName') {
      // skip RecordTypeName extra field, it is added later in that function
      return acc;
    }
    const key = getExtraFieldInputName(v.fieldApiName, v.type);
    acc[key] = mapInternalValueToFormValue(v);
    return acc;
  }, {} as Record<string, unknown>);
  if (recordTypeName) {
    formValues[recordTypeFormKey] = recordTypeName;
  }
  return formValues;
};

export const getExtraFieldInputName = (
  fieldApiName: string,
  type: FieldType,
) => {
  return `customerField:${type}:${fieldApiName}`;
};

export const parseFieldApiName = (name: string) => {
  const parts = name.split(':');
  return {
    type: parts[1] as FieldType,
    fieldApiName: parts[2],
  };
};

export function hasSeveralRecordTypes(
  availableRecordTypes: readonly AvailableRecordTypeFragment[],
) {
  return availableRecordTypes.length >= 2;
}

export function getRecordType(
  recordTypeId: string,
  availableRecordTypes: readonly AvailableRecordTypeFragment[],
): string | null {
  const recordType = availableRecordTypes.find((rt) => rt.Id === recordTypeId);
  return recordType?.DeveloperName ?? null;
}
